import { useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { BiCoin } from 'react-icons/bi';
import { toast } from 'react-toastify';

import { useCollectMutation, useGetPayoutNotPayedQuery, useGetTotalByUserQuery } from '@/app/services/package-rtk.service';
import ApiFailure from '@/components/ui/ApiFailure';
import { PSButton } from '@/components/ui/button';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import Utilities from '@/utils';

import { IProfileFormData } from '../../partner/partnerProfile';
import Pagination from './Pagination';

interface IState {
  size: string;
  page: string;
}

// Define the action types
type Action = { type: 'SET_SIZE'; payload: string } | { type: 'SET_PAGE'; payload: string };

// Define the initial state
const initialState: IState = {
  page: '1',
  size: '10',
};

// Define the reducer
const reducer = (state: IState, action: Action): IState => {
  switch (action.type) {
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'SET_SIZE':
      return { ...state, size: action.payload };
    default:
      return state;
  }
};

function PayoutRequest({ profile }: { profile: IProfileFormData }) {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialState);

  const notPayed = useGetPayoutNotPayedQuery();
  const { isLoading, data, isError, refetch, isSuccess } = useGetTotalByUserQuery({ limit: state.size, page: state.page });

  const [collectPrice, collectPriceStatus] = useCollectMutation();

  const collect = () => {
    if (notPayed?.data?.totalPrice <= 100) {
      toast.warn(t('marketplace/payment/minimunPrice'));
    } else if (!profile.payoutInfo || Object.keys(profile.payoutInfo).length === 0) {
      toast.warn(t('marketplace/payment/setupAccount'));
    } else {
      collectPrice()
        .unwrap()
        .then(() => {
          toast.success(t('marketplace/payment/requestSuccess'));
        })
        .catch(e => {
          toast.error(e?.data?.message || e?.message);
        });
    }
  };

  let content;

  if (isLoading)
    content = (
      <div className="tw-flex tw-justify-center tw-pt-10">
        <div className="tw-w-[250px] tw-animate-pulse tw-bg-gray-100 tw-rounded-xl tw-p-6">Loading ...</div>
      </div>
    );

  if (isError)
    content = (
      <div className="tw-self-stretch tw-px-6 tw-py-1 tw-bg-white tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-justify-center tw-items-center tw-gap-4 tw-inline-flex">
        <ApiFailure refetch={refetch} />
      </div>
    );

  if (isSuccess && data) {
    content = (
      <div className="tw-w-full tw-p-0 tw-flex tw-flex-col tw-gap-6 tw-relative">
        {data.packages.length === 0 ? (
          <div className="tw-text-center tw-text-gray-500 tw-py-10">{t('marketplace/transactions/noTransactions')}</div>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-6">
            <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
              {data.packages.map((pkg: any) => (
                <div
                  key={pkg.transaction_id}
                  className="tw-bg-white tw-rounded-xl tw-shadow-md tw-border tw-border-gray-200 tw-p-4 tw-flex tw-flex-col tw-gap-2 hover:tw-shadow-lg tw-transition-all tw-w-full tw-pr-10"
                >
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-flex-wrap">
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <div className="tw-flex tw-items-center tw-justify-center tw-bg-blue-50 tw-rounded-full tw-w-10 tw-h-10">
                        <BiCoin className="tw-w-5 tw-h-5 tw-text-blue-500" />
                      </div>
                      <span className="tw-font-mono tw-text-xs tw-text-gray-400">{pkg.transaction_id}</span>
                    </div>
                    <span
                      className={`tw-text-xs tw-font-semibold tw-px-2.5 tw-py-1 tw-rounded-full ${
                        pkg.payment_status === 'PAID'
                          ? 'tw-bg-green-100 tw-text-green-700'
                          : pkg.payment_status === 'PENDING'
                          ? 'tw-bg-yellow-100 tw-text-yellow-700'
                          : 'tw-bg-gray-100 tw-text-gray-700'
                      }`}
                    >
                      {Utilities.renderTransactionStatusTitle(pkg.payment_status, t)}
                    </span>
                    <div className="tw-text-sm tw-text-gray-700 tw-flex tw-items-center tw-gap-1 tw-ml-auto">
                      <span className="tw-font-semibold">{t('marketplace/transactions/price')}:</span>
                      <span className="tw-text-blue-600 tw-font-bold">${pkg.price}</span>
                    </div>
                  </div>
                  <div className="tw-text-xs tw-text-gray-500 tw-ml-14 tw-mt-1">
                    <span className="tw-font-semibold">{t('marketplace/transactions/updated')}:</span> {new Date(pkg.update_date_time).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
            <div className="">
              <Pagination pagination={data.pagination} onPageChange={page => dispatch({ type: 'SET_PAGE', payload: String(page) })} />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="tw-flex tw-flex-row tw-justify-between tw-items-center tw-mb-6">
        <div>
          <h2 className="tw-text-xl tw-font-bold tw-text-gray-800">
            {t('marketplace/transactions/totalPrice')}: <span className="tw-text-blue-600">$ {notPayed?.data?.totalPrice}</span>{' '}
          </h2>
          <div className="tw-italic tw-text-sm tw-text-gray-500">{t('marketplace/payment/description')}</div>
          <div className="tw-italic tw-text-sm tw-text-gray-500">{t('marketplace/payment/minimunPrice')}</div>
        </div>

        <PSButton
          id="btn-payout-collect"
          onClick={collect}
          isLoading={collectPriceStatus.isLoading}
          disabled={collectPriceStatus.isLoading}
          title={t('marketplace/transactions/collect')}
          className="tw-bg-blue-600 tw-text-white tw-px-6 tw-py-2 tw-rounded-lg tw-shadow hover:tw-bg-blue-700 tw-transition tw-font-semibold"
        />
      </div>
      {content}
    </div>
  );
}

export default WrappedErrorBoundary(PayoutRequest);
